import axios from "axios";
import { authService, isDefinitiveAuthError } from "./auth";
import { API_BASE } from "./api";
import { logHttp401Diagnostic } from "./blankPageDiagnostics";
import logger from "./logger";

/**
 * Global HTTP Interceptor for SynexOS
 * 
 * Protects all authenticated backend API calls across the entire frontend:
 * 1. Scoped window.fetch wrapper
 * 2. Scoped root axios interceptors
 * 
 * Seamlessly integrates with authService.refreshToken() single-flight coalescing.
 */

// Normalized backend base URL
const BACKEND_ORIGIN = (() => {
  try {
    const url = new URL(
      API_BASE || process.env.REACT_APP_BACKEND_URL,
      window.location.origin
    );
    return url.origin.toLowerCase();
  } catch {
    return "";
  }
})();

function isBackendApiUrl(url) {
  if (!url || typeof url !== "string") return false;

  const trimmed = url.trim();
  // Relative API routes
  if (trimmed.startsWith("/api/") || trimmed.startsWith("api/")) return true;

  // Absolute URLs matching backend origin
  try {
    const parsed = new URL(trimmed, window.location.origin);
    const parsedOrigin = parsed.origin.toLowerCase();
    if (parsedOrigin === BACKEND_ORIGIN) {
      return parsed.pathname.startsWith("/api/");
    }
  } catch {
    return false;
  }
  return false;
}

function isAuthEndpoint(url) {
  if (!url || typeof url !== "string") return false;
  return (
    url.includes("/auth/login") ||
    url.includes("/auth/logout") || // a 401 on logout must never trigger a refresh
    url.includes("/enterprise/auth/refresh") ||
    url.includes("/auth/refresh")
  );
}

function isPublicPageOrUrl(url) {
  const path = window.location.pathname;
  const isExcludedPage =
    path.includes("/proposal/respond/") ||
    path.includes("/contract/sign/") ||
    path.includes("/onboarding/") ||
    path.includes("/client-portal") ||
    path.includes("/internal") ||
    path.includes("/auth/login") ||
    path.includes("/auth/signup");

  if (isExcludedPage) return true;
  if (!url || typeof url !== "string") return false;
  return (
    url.includes("/proposal/respond/") ||
    url.includes("/contract/sign/") ||
    url.includes("/onboarding/") ||
    url.includes("/api/client-portal/") ||
    url.includes("/api/internal/") ||
    url.includes("/public/") ||
    url.includes("/capture/lead/")
  );
}

function getRequestToken(headers) {
  if (!headers) return null;
  let authHeader = null;
  if (headers instanceof Headers) {
    authHeader = headers.get("Authorization") || headers.get("authorization");
  } else if (Array.isArray(headers)) {
    const found = headers.find(([k]) => k && k.toLowerCase() === "authorization");
    authHeader = found ? found[1] : null;
  } else if (typeof headers === "object") {
    authHeader = headers.Authorization || headers.authorization || null;
  }
  if (authHeader && typeof authHeader === "string" && authHeader.startsWith("Bearer ")) {
    const raw = authHeader.substring(7).trim();
    if (raw && raw !== "null" && raw !== "undefined") {
      return raw;
    }
  }
  return null;
}

function applyAuthHeader(headers, token) {
  if (!token) return headers;
  const authVal = `Bearer ${token}`;

  if (headers instanceof Headers) {
    headers.set("Authorization", authVal);
    return headers;
  }
  if (Array.isArray(headers)) {
    const next = headers.filter(([k]) => k && k.toLowerCase() !== "authorization");
    next.push(["Authorization", authVal]);
    return next;
  }
  if (headers && typeof headers === "object") {
    return { ...headers, Authorization: authVal };
  }
  return { Authorization: authVal };
}

export function installGlobalHttpInterceptor() {
  if (typeof window === "undefined") return;
  if (window.__SYNEX_HTTP_INTERCEPTOR_INSTALLED__) return;
  window.__SYNEX_HTTP_INTERCEPTOR_INSTALLED__ = true;

  logger.debug("[HTTP Interceptor] Initializing scoped global fetch and axios interceptor layer");

  // ──────────────────────────────────────────────────────────────────────────
  // 1. SCOPED NATIVE FETCH INTERCEPTOR
  // ──────────────────────────────────────────────────────────────────────────
  const originalFetch = window.fetch.bind(window);

  window.fetch = async function synexInterceptedFetch(input, init = {}) {
    // Determine request URL
    let url = "";
    if (typeof input === "string") {
      url = input;
    } else if (input instanceof URL) {
      url = input.toString();
    } else if (input && typeof input === "object" && input.url) {
      url = input.url;
    }

    // Only intercept backend API endpoints
    const isBackend = isBackendApiUrl(url);
    const isAuth = isAuthEndpoint(url);
    const isPublic = isPublicPageOrUrl(url);

    // Auto-inject Authorization header if missing on backend requests
    let effectiveInit = { ...init };
    if (isBackend && !isAuth && !isPublic) {
      const currentToken = authService.getToken();
      if (currentToken) {
        const existingToken = getRequestToken(effectiveInit.headers) || (input instanceof Request ? getRequestToken(input.headers) : null);
        if (!existingToken) {
          effectiveInit.headers = applyAuthHeader(effectiveInit.headers || (input instanceof Request ? input.headers : {}), currentToken);
        }
      }
    }

    // Clone request input if Request object with body to allow safe replay
    let requestCloneForRetry = null;
    if (isBackend && !isAuth && !isPublic && input instanceof Request) {
      try {
        if (!input.bodyUsed) {
          requestCloneForRetry = input.clone();
        }
      } catch (_) {
        requestCloneForRetry = null;
      }
    }

    // Execute initial fetch
    let response;
    try {
      response = await originalFetch(input, effectiveInit);
    } catch (networkError) {
      // Pass network/abort errors directly
      throw networkError;
    }

    // Check for 401 Unauthorized on backend API
    if (response.status === 401 && isBackend && !isAuth && !isPublic && !effectiveInit._retry) {
      logHttp401Diagnostic({
        client: "fetch",
        url,
        method: effectiveInit.method || "GET",
        aborted: Boolean(effectiveInit.signal?.aborted || input?.signal?.aborted),
        hasRefreshToken: authService.hasRefreshToken(),
        isRetry: Boolean(effectiveInit._retry),
      });

      // Check if aborted before initiating refresh
      if (effectiveInit.signal?.aborted || input?.signal?.aborted) {
        logHttp401Diagnostic({ client: "fetch", url, outcome: "aborted_ignored" });
        return response;
      }

      // Check if another concurrent request already refreshed the token
      const currentToken = authService.getToken();
      const sentToken = getRequestToken(effectiveInit.headers) || (input instanceof Request ? getRequestToken(input.headers) : null);

      if (currentToken && sentToken && currentToken !== sentToken && !effectiveInit._staleTokenRetried) {
        logger.debug(`[HTTP Interceptor:Fetch] 401 on stale token. Retrying with latest token for ${url}`);
        logHttp401Diagnostic({ client: "fetch", url, outcome: "stale_token_retried" });
        effectiveInit._staleTokenRetried = true;
        effectiveInit.headers = applyAuthHeader(effectiveInit.headers, currentToken);
        const retryInput = requestCloneForRetry ? requestCloneForRetry : input;
        return originalFetch(retryInput, effectiveInit);
      }

      // Check if user has a refresh token stored
      if (!authService.hasRefreshToken()) {
        logger.warn(`[HTTP Interceptor:Fetch] 401 on ${url} and no refresh_token stored. Logging out...`);
        logHttp401Diagnostic({ client: "fetch", url, outcome: "logout_no_refresh_token" });
        authService.logout();
        if (!window.location.pathname.includes("/auth/login")) {
          window.location.href = "/auth/login";
        }
        return response;
      }

      logger.debug(`[HTTP Interceptor:Fetch] 401 detected on ${url}. Awaiting single-flight token refresh...`);
      logHttp401Diagnostic({ client: "fetch", url, outcome: "awaiting_single_flight_refresh" });

      // Execute single-flight refresh in isolated try/catch
      let newToken;
      try {
        newToken = await authService.refreshToken();
        logHttp401Diagnostic({ client: "fetch", url, outcome: "refresh_succeeded_retrying" });
        logger.debug(`[HTTP Interceptor:Fetch] Token refresh succeeded. Retrying ${url}...`);
      } catch (refreshErr) {
        // Safe check: verify if another tab has stored a valid unexpired access token
        const latestToken = authService.getToken();
        if (latestToken && !authService.isTokenExpired(latestToken)) {
          newToken = latestToken;
          logHttp401Diagnostic({ client: "fetch", url, outcome: "refresh_failed_using_other_tab_token" });
        } else if (isDefinitiveAuthError(refreshErr)) {
          logger.warn(`[HTTP Interceptor:Fetch] Definitive token refresh failure for ${url}. Logging out...`, refreshErr?.message || "Auth error");
          logHttp401Diagnostic({ client: "fetch", url, outcome: "logout_refresh_failed", error: refreshErr?.message });
          authService.logout();
          if (!window.location.pathname.includes("/auth/login")) {
            window.location.href = "/auth/login";
          }
          return response;
        } else {
          logger.warn(`[HTTP Interceptor:Fetch] Transient refresh failure for ${url}, skipping logout:`, refreshErr?.message || "Transient error");
          return response;
        }
      }

      // Check if aborted while refresh was pending
      if (effectiveInit.signal?.aborted || input?.signal?.aborted) {
        return response;
      }

      // Prepare retry options
      effectiveInit._retry = true;
      effectiveInit.headers = applyAuthHeader(effectiveInit.headers, newToken);

      let retryInput = input;
      if (input instanceof Request) {
        if (requestCloneForRetry && !requestCloneForRetry.bodyUsed) {
          retryInput = new Request(requestCloneForRetry, { headers: effectiveInit.headers });
        } else {
          // If body was already consumed and un-clonable, don't corrupt fetch; return original response
          if (input.bodyUsed) {
            return response;
          }
          retryInput = new Request(input, { headers: effectiveInit.headers });
        }
      }

      // Retry request OUTSIDE the refresh try/catch
      return originalFetch(retryInput, effectiveInit);
    }

    return response;
  };

  // ──────────────────────────────────────────────────────────────────────────
  // 2. SCOPED ROOT AXIOS INTERCEPTOR
  // ──────────────────────────────────────────────────────────────────────────
  axios.interceptors.request.use(
    (config) => {
      const url = config.url || "";
      if (isBackendApiUrl(url) && !isAuthEndpoint(url) && !isPublicPageOrUrl(url)) {
        const token = authService.getToken();
        const authHeader = config.headers?.Authorization || config.headers?.authorization;
        if (token && (!authHeader || authHeader === "Bearer null" || authHeader === "Bearer undefined")) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (error.response?.status === 401 && originalRequest) {
        const url = originalRequest.url || "";
        const isBackend = isBackendApiUrl(url);
        const isAuth = isAuthEndpoint(url);
        const isPublic = isPublicPageOrUrl(url);

        if (!isBackend || isAuth || isPublic || originalRequest._retry) {
          if (isBackend && !isAuth && !isPublic && originalRequest._retry) {
            logger.warn(`[HTTP Interceptor:Axios] 401 Unauthorized (retry failed) on ${url}.`);
          }
          return Promise.reject(error);
        }

        // Stale-token retry check
        const currentToken = authService.getToken();
        let requestToken = null;
        if (originalRequest.headers) {
          if (typeof originalRequest.headers.get === "function") {
            requestToken = originalRequest.headers.get("Authorization") || originalRequest.headers.get("authorization");
          } else {
            requestToken = originalRequest.headers["Authorization"] || originalRequest.headers["authorization"];
          }
        }
        if (requestToken && requestToken.startsWith("Bearer ")) {
          requestToken = requestToken.substring(7);
        }

        if (currentToken && requestToken && currentToken !== requestToken && !originalRequest._staleTokenRetried) {
          logger.debug(`[HTTP Interceptor:Axios] 401 on stale token. Retrying with latest token for ${url}`);
          originalRequest._staleTokenRetried = true;
          if (originalRequest.headers?.set) {
            originalRequest.headers.set("Authorization", `Bearer ${currentToken}`);
          } else {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers["Authorization"] = `Bearer ${currentToken}`;
          }
          return axios(originalRequest);
        }

        if (!authService.hasRefreshToken()) {
          authService.logout();
          if (!window.location.pathname.includes("/auth/login")) {
            window.location.href = "/auth/login";
          }
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        let newToken;
        try {
          logger.debug(`[HTTP Interceptor:Axios] 401 on ${url}. Awaiting single-flight token refresh...`);
          newToken = await authService.refreshToken();
          logger.debug(`[HTTP Interceptor:Axios] Token refresh succeeded. Retrying ${url}...`);
        } catch (refreshErr) {
          // Safe check: verify if another tab has stored a valid unexpired access token
          const latestToken = authService.getToken();
          if (latestToken && !authService.isTokenExpired(latestToken)) {
            newToken = latestToken;
          } else if (isDefinitiveAuthError(refreshErr)) {
            logger.warn(`[HTTP Interceptor:Axios] Definitive token refresh failure for ${url}. Logging out...`, refreshErr?.message || "Auth error");
            authService.logout();
            if (!window.location.pathname.includes("/auth/login")) {
              window.location.href = "/auth/login";
            }
            return Promise.reject(refreshErr);
          } else {
            logger.warn(`[HTTP Interceptor:Axios] Transient refresh failure for ${url}, skipping logout:`, refreshErr?.message || "Transient error");
            return Promise.reject(refreshErr);
          }
        }

        if (originalRequest.headers?.set) {
          originalRequest.headers.set("Authorization", `Bearer ${newToken}`);
        } else {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        }

        // Retry OUTSIDE refresh try/catch
        return axios(originalRequest);
      }
      return Promise.reject(error);
    }
  );
}
